import { ArrowLeftOutlined, LoginOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Input, List, Spin, Typography } from "antd";
import { ReactElement, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { useSession } from "@lib/authentication/SessionHooks";
import { discover, type DiscoveryMatch } from "@common/auth/DiscoveryService";
import { maintenanceMinutes, maintenanceRetryAfter } from "@common/rest/maintenance";
import { clearSubTenants } from "@common/auth/SubTenantStore";
import {
  clearPersistedAuthority,
  clearPersistedLoginHint,
  getPersistedAuthority,
  getPersistedLoginHint,
  getUserManager,
  initUserManager,
  persistAuthority,
  persistLoginHint,
} from "@common/auth/userManager";
import s from "./LoginPage.module.scss";

/**
 * Login multi-tenant, identifier-first. L'utente digita un identificativo, la
 * Home Realm Discovery risolve il/i realm e — SOLO su azione esplicita — si
 * procede al login OIDC su Keycloak (Authorization Code + PKCE), passando
 * `login_hint` così lo username è precompilato.
 *
 * Il redirect a Keycloak NON è mai automatico all'atterraggio su /login: evita
 * il loop dopo un login fallito (es. realm decoy `mydriveby-void` per un
 * identificativo errato). L'authority persistita serve solo a main.tsx per il
 * callback e il ripristino sessione.
 */
export function LoginPage(): ReactElement {
  const { t } = useTranslation();
  const { initialized, session } = useSession();
  const authority = getPersistedAuthority();

  const [login, setLogin] = useState(getPersistedLoginHint() ?? "");
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matches, setMatches] = useState<DiscoveryMatch[] | null>(null);
  const [sessionError, setSessionError] = useState(false);
  const checked = useRef(false);

  // Realm già noto ma sessione app assente: NON reindirizzare automaticamente
  // (eviterebbe il loop). Se però esiste un token OIDC valido e /me è fallita,
  // segnala l'errore con una via d'uscita. `initialized` = OIDC assestato.
  useEffect(() => {
    if (!authority || checked.current) return;
    if (!initialized || session.authenticated) return;
    const um = getUserManager();
    if (!um) return;
    checked.current = true;
    void um.getUser().then((user) => {
      if (user && !user.expired) setSessionError(true);
    });
  }, [authority, initialized, session.authenticated]);

  // Il dispatcher decide: realm platform → /console, tenant → /app/dashboard.
  if (session.authenticated) return <Navigate to="/" />;

  function proceed(match: DiscoveryMatch): void {
    const hint = login.trim();
    persistAuthority(match.authority);
    persistLoginHint(hint);
    setRedirecting(true);
    // Redirect al Keycloak del realm scelto, con username precompilato.
    void initUserManager(match.authority).signinRedirect({ login_hint: hint });
  }

  async function onSubmit(): Promise<void> {
    const value = login.trim();
    if (!value) return;
    setLoading(true);
    setError(null);
    try {
      const found = await discover(value);
      if (found.length === 0) {
        setError(t("loginpage.identifier.notFound"));
      } else if (found.length === 1) {
        proceed(found[0]);
      } else {
        setMatches(found);
      }
    } catch (e) {
      // Axios grezzo, fuori dall'interceptor di RestAPI: il 503 di manutenzione va
      // riconosciuto qui, o si presenterebbe come servizio irraggiungibile.
      const retryAfter: number | null = maintenanceRetryAfter(e);
      setError(retryAfter === null
        ? t("loginpage.identifier.error")
        : t("system.maintenance.notice", { minutes: maintenanceMinutes(retryAfter) }));
    } finally {
      setLoading(false);
    }
  }

  // Ripartenza pulita da identifier-first (realm sbagliato / altro utente).
  function useAnotherIdentifier(): void {
    clearPersistedAuthority();
    clearPersistedLoginHint();
    clearSubTenants();
    window.location.reload();
  }

  function renderBody(): ReactElement {
    if (sessionError) {
      return (
        <div className={s.actions}>
          <Alert type="error" showIcon description={t("loginpage.sessionError")} className={s.alert} />
          <Button type="link" className={s.hint} onClick={useAnotherIdentifier}>
            {t("loginpage.useAnotherIdentifier")}
          </Button>
        </div>
      );
    }
    if (redirecting) {
      return (
        <div className={s.actions}>
          <Spin size="large" />
          <Button type="link" className={s.hint} onClick={useAnotherIdentifier}>
            {t("loginpage.useAnotherIdentifier")}
          </Button>
        </div>
      );
    }
    if (matches) {
      return (
        <div className={s.actions}>
          <Typography.Text strong>{t("loginpage.identifier.chooseCustomer")}</Typography.Text>
          <List
            bordered
            className={s.matchList}
            dataSource={matches}
            renderItem={(m) => (
              <List.Item className={s.matchItem} onClick={() => proceed(m)} actions={[<LoginOutlined key="go" />]}>
                {m.customerName || m.realm}
              </List.Item>
            )}
            footer={
              <List.Item className={s.matchItem} onClick={() => setMatches(null)} actions={[<ArrowLeftOutlined />]}>
                {t("loginpage.identifier.back")}
              </List.Item>
            }
          />
        </div>
      );
    }
    return (
      <div className={s.actions}>
        <Input
          size="large"
          autoFocus
          placeholder={t("loginpage.identifier.placeholder")}
          value={login}
          disabled={loading}
          onChange={(e) => setLogin(e.target.value)}
          onPressEnter={() => void onSubmit()}
        />
        <Button
          type="primary"
          size="large"
          icon={loading ? <Spin size="small" /> : <LoginOutlined />}
          block
          disabled={loading || !login.trim()}
          onClick={() => void onSubmit()}
        >
          {t("loginpage.identifier.continue")}
        </Button>
        <Typography.Text type="secondary" className={s.hint}>
          {t("loginpage.hint")}
        </Typography.Text>
      </div>
    );
  }

  return (
    <div className={s.container}>
      <Card className={s.card} variant="borderless">
        <div className={s.header}>
          <Typography.Title level={3} className={s.title}>
            {t("loginpage.title")}
          </Typography.Title>
          <Typography.Text type="secondary">
            {redirecting ? t("loginpage.subtitle") : t("loginpage.identifier.subtitle")}
          </Typography.Text>
        </div>

        {error && <Alert type="error" showIcon description={error} className={s.alert} />}

        {renderBody()}

      </Card>
    </div>
  );
}
